import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { dutyStationService } from '../services/dutyStationService';

/**
 * DutyStationDropdown - A reusable dropdown component to select duty stations
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Input field name
 * @param {string} props.value - Selected duty station value
 * @param {function} props.onChange - Function to call when selection changes
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.label - Label text for the dropdown
 * @param {boolean} props.isInvalid - Whether the field is invalid (for validation)
 * @param {string} props.invalidFeedback - Feedback message for invalid state
 */
const DutyStationDropdown = ({
                                 name = 'dutyStation',
                                 value = '',
                                 onChange,
                                 required = false,
                                 label = 'Duty Station',
                                 isInvalid = false,
                                 invalidFeedback = 'Please select a duty station',
                                 placeholder = 'Select a duty station'
                             }) => {
    const [dutyStations, setDutyStations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch duty stations for dropdown
    useEffect(() => {
        const fetchDutyStations = async () => {
            try {
                setLoading(true);
                const data = await dutyStationService.getDutyStationsForDropdown();
                setDutyStations(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching duty stations for dropdown', err);
                setError('Could not load duty stations');
            } finally {
                setLoading(false);
            }
        };

        fetchDutyStations();
    }, []);

    // Handle change and call parent onChange
    const handleChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label>{label}</Form.Label>}
            <Form.Select
                name={name}
                value={value}
                onChange={handleChange}
                required={required}
                isInvalid={isInvalid}
                disabled={loading}
            >
                <option value="">{loading ? 'Loading duty stations...' : placeholder}</option>
                {dutyStations.map((dutyStation) => (
                    <option key={dutyStation.id} value={dutyStation.id}>
                        {dutyStation.name}
                    </option>
                ))}
            </Form.Select>
            {error && <div className="text-danger small mt-1">{error}</div>}
            {isInvalid && <Form.Control.Feedback type="invalid">{invalidFeedback}</Form.Control.Feedback>}
        </Form.Group>
    );
};

DutyStationDropdown.propTypes = {
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    required: PropTypes.bool,
    label: PropTypes.string,
    isInvalid: PropTypes.bool,
    invalidFeedback: PropTypes.string,
    placeholder: PropTypes.string
};

export default DutyStationDropdown;